import { Box, Collapse } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ChevronDown, ChevronUp, Scales } from '@sweep-io/sweep-design/dist/icons';
import { Button, Typography } from '@sweep-io/sweep-design';
import { DedupMatchingType } from '../../../types/enums/DedupMatchingType';
import { useSweepFields } from '../../../sweep-fields/useCachedSweepFields';
import { useRunOnce } from '../../common/useRunOnce';
import { SelectorValueTypes } from '../../../types/enums/SelectorValueTypes';
import { settingsPanelLayout } from './helpers';
import { PriorityRulesTable } from './PriorityRulesTable';
import { PriorityRuleDialog } from './PriorityRuleDialog';
import { uniqueId } from '../../../lib/uniqueId';
import { DataTableId } from '../../common/table/utils';
import cloneDeep from 'lodash/cloneDeep';
import { SweepFieldOperator } from '../../SweepFieldsOperatorSelector/sweepFieldsTypesAndOperators';
import { populateFieldsInfoFromSweepFields } from '../../../sweep-fields/sweepFieldsInfoReducer';

const MULTIPLE_CRITERIA_MAX_MIN_ERROR =
  'Rules with "is latest", "is earliest", "is greatest", or "is lowest" operators cannot have multiple conditions';

const validateTieBreakerRule = (rule: PriorityRule): string | null => {
  const criteria = rule.when?.criteria || [];
  if (criteria.length < 2) {
    return null;
  }
  // Check if at least one criterion uses MAX or MIN operator
  const hasMaxMinOperator = criteria.some(
    (c) => c.operator === SweepFieldOperator.MAX || c.operator === SweepFieldOperator.MIN,
  );
  return hasMaxMinOperator ? MULTIPLE_CRITERIA_MAX_MIN_ERROR : null;
};

interface SettingsTieBreakersPanelProps {
  readonly?: boolean;
  action: MatchingActionParams | DeduplicationActionParams;
  crmOrgId: string;
  objectType: string;
  type: DedupMatchingType;
  onChange: (rules?: PriorityRule[]) => void;
}

const labelsByType = {
  [DedupMatchingType.LEAD_TO_LEAD_DEDUP]: {
    subTitle:
      'Set up Tie breakers to determine the best match when there are multiple duplicates to a lead',
  },
  [DedupMatchingType.LEAD_TO_CONTACT_DEDUP]: {
    subTitle:
      'Set up Tie breakers to determine the best match when there are multiple duplicates to a lead',
  },
  [DedupMatchingType.LEAD_TO_ACCOUNT_MATCHING]: {
    subTitle:
      'Set up Tie breakers to determine the best match when there are multiple account matches to a lead',
  },
  [DedupMatchingType.LEAD_TO_LEAD_MATCHING]: {
    subTitle: 'Set up Tie breakers to determine the best match when there are multiple options',
  },
  [DedupMatchingType.ACCOUNT_TO_ACCOUNT_DEDUP]: {
    subTitle:
      'Set up Tie breakers to determine the best match when there are multiple duplicates to an account',
  },
  [DedupMatchingType.CONTACT_TO_CONTACT_DEDUP]: {
    subTitle:
      'Set up Tie breakers to determine the best match when there are multiple duplicates to a contact',
  },
  [DedupMatchingType.CASE_TO_CASE_DEDUP]: {
    subTitle:
      'Set up Tie breakers to determine the best match when there are multiple duplicates to a case',
  },
  [DedupMatchingType.OPP_TO_OPP_DEDUP]: {
    subTitle:
      'Set up Tie breakers to determine the best match when there are multiple duplicates to an opportunity',
  },
};

const LAST_MODIFIED_DATE = 'Last Modified Date';

const SettingsTieBreakersPanel = ({
  readonly,
  action,
  crmOrgId,
  objectType,
  type,
  onChange,
}: SettingsTieBreakersPanelProps) => {
  const dispatch = useDispatch();
  const { searchSweepFields } = useSweepFields();
  const [objectFields, setObjectFields] = useState<SweepField[]>();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);

  const getObjectFields = async () => {
    const { sweepFields } = await searchSweepFields({
      objectType: [objectType],
      crmOrgId,
    });
    const fields = sweepFields[objectType].fields;
    setObjectFields(fields);
    dispatch(populateFieldsInfoFromSweepFields({ sweepFields: fields }));
  };

  const tieBreakers = action?.tieBreakers ?? [];

  const createDefaultRule = useCallback((): PriorityRule => {
    const currentField = objectFields?.find((el) => el.sweepFieldName === LAST_MODIFIED_DATE);

    if (currentField && currentField.id) {
      return {
        _id: uniqueId(),
        name: 'Tie breaker for ' + objectType,
        when: {
          criteriaLogic: '1',
          criteria: [
            {
              criterionId: currentField.id,
              _fieldIds: [currentField.id],
              operator: SweepFieldOperator.MAX,
              value: 'true',
              valueType: SelectorValueTypes.LITERAL,
              fieldType: currentField.sfDisplayType,
            },
          ],
        },
      };
    }

    return {
      _id: uniqueId(),
      name: 'Tie breaker for ' + objectType,
      when: {
        criteriaLogic: '1',
        criteria: [],
      },
    };
  }, [objectFields, objectType]);

  const setDefaultRule = useCallback(() => {
    if (tieBreakers.length === 0 && objectFields) {
      const defaultRule = createDefaultRule();
      onChange([defaultRule]);
    }
  }, [tieBreakers.length, objectFields, createDefaultRule, onChange]);

  const [isPanelExpanded, setIsPanelExpanded] = useState(true);

  useRunOnce(async () => {
    await getObjectFields();
  });

  useEffect(() => {
    setDefaultRule();
  }, [setDefaultRule]);

  const handleNewRule = () => {
    setEditingRuleId(null);
    setDialogOpen(true);
  };

  const handleEditRule = (ruleId: string) => {
    setEditingRuleId(ruleId);
    setDialogOpen(true);
  };

  const handleDuplicateRule = (ruleId: string) => {
    const ruleToDuplicate = tieBreakers.find((r) => r._id === ruleId);
    if (ruleToDuplicate) {
      const duplicatedRule: PriorityRule = {
        ...cloneDeep(ruleToDuplicate),
        _id: uniqueId(),
        name: `${ruleToDuplicate.name} (copy)`,
      };
      onChange([...tieBreakers, duplicatedRule]);
    }
  };

  const handleSaveRule = (rule: PriorityRule) => {
    if (editingRuleId) {
      const updatedRules = tieBreakers.map((r) => (r._id === editingRuleId ? rule : r));
      onChange(updatedRules);
    } else {
      onChange([...tieBreakers, rule]);
    }
    setDialogOpen(false);
    setEditingRuleId(null);
  };

  const handleCancel = () => {
    setDialogOpen(false);
    setEditingRuleId(null);
  };

  const editingRule = editingRuleId ? tieBreakers.find((r) => r._id === editingRuleId) : undefined;

  return (
    <Box sx={{ ...settingsPanelLayout }}>
      <Collapse in={isPanelExpanded} collapsedSize={24}>
        <Box className="panelHeader" onClick={() => setIsPanelExpanded(!isPanelExpanded)}>
          <Box sx={{ display: 'flex', gap: '16px' }}>
            <Scales />
            <Box sx={{ display: 'flex', flexDirection: 'column', marginTop: '2px' }}>
              <Typography variant="body-bold">Tie breakers</Typography>
              <Typography variant="body">{labelsByType[type].subTitle}</Typography>
            </Box>
          </Box>
          <Box>{isPanelExpanded ? <ChevronUp size="medium" /> : <ChevronDown size="medium" />}</Box>
        </Box>
        <Box sx={{ marginTop: '16px', marginLeft: '44px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <PriorityRulesTable
              rules={tieBreakers}
              onRulesChange={onChange}
              onRuleEdit={handleEditRule}
              onRuleDuplicate={handleDuplicateRule}
              readonly={readonly}
              tableId={DataTableId.TieBreakersTable}
            />
            {!readonly && (
              <Box>
                <Button
                  variant="flat-blue"
                  size="small"
                  startIconName="Plus"
                  onClick={handleNewRule}
                >
                  New rule
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Collapse>
      {dialogOpen && (
        <PriorityRuleDialog
          initialRule={editingRule}
          onCreateEdit={handleSaveRule}
          crmOrgId={crmOrgId}
          objectType={objectType}
          onCancel={handleCancel}
          defaultRuleName={'Tie breaker for ' + objectType}
          validateRule={validateTieBreakerRule}
        />
      )}
    </Box>
  );
};

export default SettingsTieBreakersPanel;
